import React from 'react';

import {
  ArrowDropDownOutlined as ArrowDropDown,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  accordionDetailsClasses,
  accordionSummaryClasses,
  Box,
  Button,
  Link,
  Paper,
  StepContent,
  StepLabel,
  SvgIcon,
  Typography,
} from '@mui/material';

import { arrayJoin } from '@hrlogics/helper-utils';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, getDateTimeInTimezone, getUserFullName } from '@clearverify/utils';

import { type ActivityLogItemFragment, ActivityLogType, ActivityLogVisibleFor } from 'src/api';
import { CheckIcon, CloseIcon, CommentIcon, DownloadIcon, InfoIcon, WatchIcon } from 'src/assets';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { ModalId } from 'src/constants/modals';
import { useActivityLogDeleteAction } from 'src/hooks/activityLog';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { usePlatformStaff } from 'src/hooks/platformStaff';
import { useModalV2 } from 'src/hooks/shared';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { EntityActionConfirmationModal } from 'src/modals/common';
import type { SxProp } from 'src/types/mui';
import { convertFileSize } from 'src/utils/file';

interface ActivityLogItemProps {
  activityLog: ActivityLogItemFragment;
  /** See ActivityLogsList — renders the entry without the author / company sub-text. */
  sanitized?: boolean;
}

export const ActivityLogItem: React.FC<ActivityLogItemProps> = ({ activityLog, sanitized }) => {
  const { openModal } = useModalV2();
  const inboxItemId = useInboxItemParamId();

  const { timezone } = useCurrentUser();

  const { hasPlatformRoles } = useCurrentUserPermissions();
  const { platformStaff } = usePlatformStaff(activityLog.platformStaffId, {
    skip: !hasPlatformRoles,
  });

  const { deleteActivityLog, deleting } = useActivityLogDeleteAction(activityLog.id, inboxItemId);

  const logType = React.useMemo(() => activityLog.type, [activityLog]);

  const icon = React.useMemo(() => {
    return <SvgIcon sx={defaultIconStyles} component={iconsByLogType[logType]} inheritViewBox />;
  }, [logType]);

  const [timeString, dateString] = React.useMemo(() => {
    const createdDateTimeUserTimezone = getDateTimeInTimezone(
      activityLog.createdAt,
      timezone,
    )?.toISO();
    return [
      formatDate(createdDateTimeUserTimezone, DateFormatPatterns.meridiemTimePadded),
      formatDate(createdDateTimeUserTimezone, DateFormatPatterns.shortMonthDayYearAlt),
    ];
  }, [activityLog.createdAt, timezone]);

  const handleDelete = React.useCallback(() => {
    openModal(EntityActionConfirmationModal, {
      id: ModalId.EntityActionConfirmation,
      action: 'delete',
      entity: 'this Activity Log item',
      onSubmit: async () => {
        await deleteActivityLog();
      },
    });
  }, [deleteActivityLog, openModal]);

  // Sanitized timeline for Employer / PEO / PEO PP on Verification Requests (CLEAR-2293): the
  // heading already arrives generic — render it alone. Admin notes keep the regular UI.
  const isSanitizedEntry = !!sanitized && activityLog.type !== ActivityLogType.AdminNoteAdded;

  const renderActivityLogHeading = React.useCallback(() => {
    if (isSanitizedEntry) {
      return (
        <Paper elevation={0} sx={paperStyles}>
          <Typography component="span" variant="body2">
            {activityLog.heading}
          </Typography>
        </Paper>
      );
    }

    return (
      <React.Fragment>
        {activityLog.comment ? (
          <Accordion sx={accordionStyles} elevation={0}>
            <AccordionSummary sx={accordionStyles} expandIcon={<ArrowDropDown />}>
              <Box>
                <Typography
                  component="span"
                  variant="body2"
                  sx={theme => ({ color: theme.palette.primary.main })}
                >
                  {activityLog.authorDisplayName ? (
                    <DemoPrivacyText>{activityLog.authorDisplayName}</DemoPrivacyText>
                  ) : null}{' '}
                </Typography>

                <Typography component="span" variant="body2">
                  <DemoPrivacyText>{activityLog.heading}</DemoPrivacyText>
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                sx={theme => ({ color: theme.palette.text.secondary, overflowWrap: 'break-word' })}
              >
                {activityLog.comment ? (
                  <DemoPrivacyText>{activityLog.comment}</DemoPrivacyText>
                ) : null}
              </Typography>
              <Box>
                {activityLog.file && (
                  <Link
                    key={activityLog.file.id}
                    href={activityLog.file.downloadUrl || ''}
                    target="_blank"
                    download
                    rel="noreferrer"
                    sx={fileStyles}
                  >
                    <InsertDriveFileIcon fontSize="medium" color="action" />
                    <Box display="flex" flexDirection="column" ml={1}>
                      <Typography variant="body3" fontWeight="500">
                        {activityLog.file.filename}
                      </Typography>
                      <Typography variant="body3">
                        {' '}
                        {convertFileSize(activityLog.file.size || 0).toUpperCase()}
                      </Typography>
                    </Box>
                  </Link>
                )}
              </Box>
            </AccordionDetails>
            {platformStaff?.user && activityLog.type === ActivityLogType.AdminNoteAdded && (
              <AccordionActions>
                <Button size="small" onClick={handleDelete} loading={deleting}>
                  Delete
                </Button>
              </AccordionActions>
            )}
          </Accordion>
        ) : (
          <Paper elevation={0} sx={paperStyles}>
            {activityLog.authorDisplayName && (
              <Typography
                component="span"
                variant="body2"
                sx={theme => ({ color: theme.palette.primary.dark })}
              >
                <DemoPrivacyText>{activityLog.authorDisplayName}</DemoPrivacyText>{' '}
              </Typography>
            )}
            <Typography component="span" variant="body2">
              <DemoPrivacyText>{activityLog.heading}</DemoPrivacyText>
            </Typography>{' '}
          </Paper>
        )}

        {platformStaff?.user && activityLog.type === ActivityLogType.AdminNoteAdded ? (
          <Box mt={0.75}>
            <Typography
              component="span"
              variant="body3"
              sx={theme => ({ color: theme.palette.text.secondary })}
            >
              From
            </Typography>{' '}
            <Typography
              component="span"
              variant="body3"
              sx={theme => ({ color: theme.palette.text.primary })}
            >
              <DemoPrivacyText>
                {getUserFullName(platformStaff?.user) || activityLog.authorDisplayName}
              </DemoPrivacyText>
            </Typography>
            {', '}
            <Typography
              component="span"
              variant="body3"
              sx={theme => ({ color: theme.palette.text.secondary })}
            >
              visible for
            </Typography>{' '}
            <Typography
              component="span"
              variant="body3"
              sx={theme => ({ color: theme.palette.text.primary })}
            >
              {formatVisibleFor(activityLog.visibleFor)}
            </Typography>
          </Box>
        ) : (
          <Box mt={0.75}>
            {activityLog.companyDisplayName ? (
              <React.Fragment>
                <Typography
                  component="span"
                  variant="body3"
                  sx={theme => ({ color: theme.palette.text.secondary })}
                >
                  From company:
                </Typography>{' '}
                <Typography
                  component="span"
                  variant="body3"
                  sx={theme => ({ color: theme.palette.text.primary })}
                >
                  <DemoPrivacyText>{activityLog.companyDisplayName}</DemoPrivacyText>
                </Typography>
              </React.Fragment>
            ) : (
              <Typography
                component="span"
                variant="body3"
                sx={theme => ({ color: theme.palette.text.secondary })}
              >
                Employee
              </Typography>
            )}
          </Box>
        )}
      </React.Fragment>
    );
  }, [
    activityLog.authorDisplayName,
    activityLog.comment,
    activityLog.companyDisplayName,
    activityLog.heading,
    activityLog.file,
    activityLog.type,
    activityLog.visibleFor,
    platformStaff?.user,
    handleDelete,
    deleting,
    isSanitizedEntry,
  ]);

  return (
    <React.Fragment>
      <StepLabel sx={{ padding: 0 }} slotProps={{ stepIcon: { icon } }}>
        <Typography
          marginLeft="12px"
          variant="body2"
          sx={theme => ({ color: theme.palette.text.secondary })}
        >
          {dateString} at {timeString?.toLowerCase()}
        </Typography>
      </StepLabel>
      <StepContent>{renderActivityLogHeading()}</StepContent>
    </React.Fragment>
  );
};

const paperStyles: SxProp = theme => ({
  padding: '10px 16px',
  backgroundColor: theme.palette.secondary.light,
  borderRadius: 2.2,
  mt: 1,
});

const accordionStyles: SxProp = theme => ({
  backgroundColor: theme.palette.secondary.light,
  borderRadius: '8px !important',
  mt: '5px !important',
  mb: '0 !important',

  [`.${accordionSummaryClasses.content}`]: {
    minHeight: 0,
    margin: '0 !important',
    padding: '10px 0',
  },
  [`.${accordionSummaryClasses.root}`]: {
    minHeight: '0 !important',
    margin: 0,
  },
  [`.${accordionDetailsClasses.root}`]: {
    paddingLeft: 2,
    paddingTop: 0,
  },
});

const iconsByLogType = {
  [ActivityLogType.Accepted]: CheckIcon,
  [ActivityLogType.Requested]: InfoIcon,

  [ActivityLogType.Declined]: CloseIcon,
  [ActivityLogType.Viewed]: WatchIcon,
  [ActivityLogType.Commented]: CommentIcon,
  [ActivityLogType.Downloaded]: DownloadIcon,

  [ActivityLogType.DisputeCreated]: CheckIcon,
  [ActivityLogType.Sent]: CheckIcon,

  [ActivityLogType.AdminNoteAdded]: InfoIcon,
} as const;

const defaultIconStyles: SxProp = {
  fontSize: '24px',
  '*': {
    color: 'white',
  },
};

const fileStyles: SxProp = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: '10px 0 0 0',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
};

const formatVisibleFor = (visibleFor: ActivityLogVisibleFor[]) => {
  return arrayJoin(visibleFor.map(item => ACTIVITY_LOG_VISIBLE_FOR_MAP[item]));
};

const ACTIVITY_LOG_VISIBLE_FOR_MAP = {
  [ActivityLogVisibleFor.Employee]: 'Employee',
  [ActivityLogVisibleFor.Employer]: 'Employer',
  [ActivityLogVisibleFor.PeoOrProvider]: 'PEO or Provider',
  [ActivityLogVisibleFor.Verifier]: 'Verifier',
} as const satisfies Record<ActivityLogVisibleFor, string>;
